import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099INTInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-INT instructions for Tax year 2025"
          description=" Learn how to fill out Form 1099-INT for Tax Year 2025 with clear, line-by-line instructions to report interest income accurately and stay IRS compliant."
          keywords=""
          canonical="resources/information-returns/form-1099-int-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-INT</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-int-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-INT at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-INT
                        </strong>{' '}
                        is used to report interest income paid to individuals or
                        businesses. Banks, credit unions, investment firms, and
                        other financial institutions that pay{' '}
                        <strong style={{ fontWeight: 600 }}>
                          $10 or more
                        </strong>{' '}
                        in interest during the tax year may be required to file
                        this form.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        Recipient copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}> January 31</strong>
                        , and the form must be filed with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          February 28
                        </strong>{' '}
                        if filing on paper or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          March 31
                        </strong>{' '}
                        if filing electronically.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        Late filing penalties increase based on how late the
                        form is filed, ranging from{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per return
                        </strong>
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        This article covers{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          line-by-line instructions
                        </strong>{' '}
                        for Form 1099-INT, including payer and recipient
                        details,
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Boxes 1-4
                        </strong>{' '}
                        for interest income and withholding,{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Boxes 5-13
                        </strong>{' '}
                        for foreign tax, tax-exempt interest, and bond premiums,
                        and{' '}
                        <strong style={{ fontWeight: 600 }}>Boxes 14-17</strong>{' '}
                        for state reporting.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}> Penalties:</strong>{' '}
                        Form 1099-INT late filing penalties range from{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680
                        </strong>{' '}
                        per return, based on the filing delay and intentional
                        disregard of IRS filing requirements.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full 30-day extension for furnishing
                        recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-INT?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  IRS Form 1099-INT is a tax return filed by financial
                  institutions and other organizations that pay interest income
                  to individuals or businesses. Its purpose is to report the
                  total amount of interest earned by the recipient from various
                  sources throughout the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  1099 int form serves as an official record of interest income
                  paid to a taxpayer, enabling them to properly declare this
                  income and pay any taxes owed on the interest earned during
                  the tax year.
                </Typography>
                {/* <Alert
                  severity="info"
                  sx={{ mb: 4 }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Tip:</b> Make sure you collect W-9 forms from any
                  contractors you pay so you have the necessary information to
                  complete the 1099-NEC accurately.
                </Alert> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who can file Form 1099-INT?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Any individual, business, or entity that pays interest
                  totaling $10 or more to another person or entity during the
                  tax year is required to file Form 1099-INT. This form is used
                  to report the interest payments made.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The types of organizations that must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-int/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-INT
                  </Link>{' '}
                  include:
                </Typography>
                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Banks</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Credit unions</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Investment firms</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Other financial institutions</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Businesses that pay interest on loans or deposits they
                      receive
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Essentially, any payer of interest income must report those
                  payments using Form 1099-INT if the total interest paid to a
                  single recipient equals or exceeds $10 for the tax year.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-INT due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  There are two key deadlines related to Form 1099-INT that
                  payers must be aware of:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  1. Filing deadline with IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline for payers to file Form 1099-INT with the IRS
                  depends on the filing method:
                </Typography>

                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong>E-filing deadline: March 31, 2026</strong>
                    <br /> If you&apos;re e-filing the Form 1099-INT to the IRS,
                    the deadline is March 31st of the year following the tax
                    year for which the form is being issued.
                  </li>
                  <Box
                    component="div"
                    sx={{ mb: 2, p: 2, borderRadius: 2 }}
                    className="gradient-bg"
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Avoid the last-minute 1099-INT deadline stress
                        </Typography>
                        <Typography variant="body2">
                          E-file with TaxZerone now for easy IRS compliance and
                          peace of mind.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 150,
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover, &:focus, &:active': { color: '#fff' },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099intinstructions`}
                        >
                          E-File Now
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Paper filing deadline: February 28, 2026*</strong>
                    <br /> If you&apos;re filing Form 1099-INT with the IRS
                    using traditional paper filing methods, the deadline is
                    earlier — February 28th of the year following the tax year
                    for which the form is being issued.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  2. Recipient copies deadline: January 31, 2026*
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers are required to provide copies of Form 1099-INT to the
                  recipients (individuals or entities that received interest
                  payments) by January 31 of the year following the tax year for
                  which the form is being issued. This ensures that recipients
                  have the necessary information to accurately report their
                  interest income when filing their tax returns.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  It is crucial for payers to meet these deadlines to avoid
                  potential penalties from the IRS for failure to file Form
                  1099-INT accurately and on time. Penalties may be imposed for
                  late filing, failure to provide recipient copies, or reporting
                  incorrect information on the form.
                </Typography>
                <Alert
                  severity="warning"
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  icon={false}
                >
                  <b>Note:</b> The deadline for Form 1099-INT is February 2,
                  2026, because January 31 falls on a Saturday and February 1 is
                  a Sunday, so it was extended to the next business day.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-INT penalty
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-int/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 1099-INT
                  </Link>{' '}
                  within the deadline, the IRS will impose a penalty for each
                  missed return. Below is the breakdown of Form 1099-INT
                  penalties:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Stack
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Why risk penalties?
                    </Typography>
                    <Typography variant="body2">
                      Choose TaxZerone to e-file your 1099-INT forms ahead of
                      the deadline.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 198,
                      textAlign: 'left',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099intinstructions`}
                    >
                      E-file Form 1099-INT
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-INT Instructions - How to fill out?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-INT.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Payer details
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your name or business name, complete address, and TIN
                  (SSN if you&apos;re an individual; EIN if you&apos;re a
                  business)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-payer-img.webp" />
                  <source srcSet="/assets/images/1099-int-payer-img.png" />
                  <img
                    src="/assets/images/1099-int-payer-img.png"
                    loading="lazy"
                    alt="Form 1099-int-payer details"
                    width={590}
                    height={348}
                    className="proportional-img"
                  />
                </picture>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Recipient details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-recipient-img.webp" />
                  <source srcSet="/assets/images/1099-int-recipient-img.png" />
                  <img
                    src="/assets/images/1099-int-recipient-img.png"
                    loading="lazy"
                    alt="Form 1099-int-recipient details"
                    width={590}
                    height={549}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                  Enter the recipient&apos;s name, complete address, and TIN
                  (SSN for an individual; EIN for a business).
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b> Account number:</b> You must provide the account number
                    in these two situations:
                    <ol style={{ marginBottom: 32, listStyleType: 'circle' }}>
                      <li style={{ marginBottom: 16 }}>
                        You have multiple accounts for the same recipient, and
                        you are filing more than one Form 1099-INT for that
                        recipient.
                      </li>
                      <li style={{ marginBottom: 16 }}>
                        You checked the &quot;FATCA filing requirement&quot;
                        box.
                      </li>
                    </ol>
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    <b> 2nd TIN not:</b> If the IRS has notified you two times
                    within the last 3 years that the payee provided an incorrect
                    TIN, you can enter an &quot;X&quot; in this box. By doing
                    so, the IRS will stop sending you further notices about this
                    account&apos;s TIN.
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  FATCA Filing Requirement Checkbox
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check this box if you are
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    a U.S. payer that is reporting on Form(s) 1099 (including
                    reporting payments in boxes 1, 3, 8, 9, and 10 on this Form
                    1099-INT) as part of satisfying your requirement to report
                    with respect to a U.S. account for chapter 4 purposes, as
                    described in Regulations section
                    1.1471-4(d)(2)(iii)(A).{' '}
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    a foreign financial institution (FFI) reporting payments to
                    a U.S. account pursuant to an election described in
                    Regulations section 1.1471-4(d)(5)(i)(A).
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Payer&apos;s RTN (optional)
                </Typography>

                <picture>
                  <source srcSet="/assets/images/1099-int-rtn-img.webp" />
                  <source srcSet="/assets/images/1099-int-rtn-img.png" />
                  <img
                    src="/assets/images/1099-int-rtn-img.png"
                    loading="lazy"
                    alt="Form 1099-int-payer's rtn"
                    width={277}
                    height={103}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you are a financial institution and wish to participate in
                  the program for direct deposit of refunds, enter your routing
                  transit number (RTN).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Interest Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-interset-img.webp" />
                  <source srcSet="/assets/images/1099-int-interset-img.png" />
                  <img
                    src="/assets/images/1099-int-interset-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-1-interest income"
                    width={277}
                    height={103}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the taxable interest income not included in box 3.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Early Withdrawal Penalty
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-withdraw-img.webp" />
                  <source srcSet="/assets/images/1099-int-withdraw-img.png" />
                  <img
                    src="/assets/images/1099-int-withdraw-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-2-early withdrawal penalty"
                    width={598}
                    height={113}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of interest or principal you had to forfeit
                  due to an early withdrawal from a time deposit account, such
                  as a certificate of deposit (CD). This forfeited amount is
                  deductible from your gross income by the recipient.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  DO NOT subtract the forfeited amount from the interest income
                  reported in Box 1. Report the full interest income amount in
                  Box 1 and the early withdrawal penalty separately in Box 2.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Interest on U.S. Savings Bonds and Treasury Obligations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-us-saving-bonds-img.webp" />
                  <source srcSet="/assets/images/1099-int-us-saving-bonds-img.png" />
                  <img
                    src="/assets/images/1099-int-us-saving-bonds-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-3-interest on u.s.savings bonds and treasury obligations"
                    width={598}
                    height={113}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter interest on U.S. Savings Bonds, Treasury bills, Treasury
                  notes, and Treasury bonds. DO NOT include in box 1.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Federal Income Tax Withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-federal-tax-img.webp" />
                  <source srcSet="/assets/images/1099-int-federal-tax-img.png" />
                  <img
                    src="/assets/images/1099-int-federal-tax-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-4-federal income tax withheld"
                    width={277}
                    height={103}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any federal income tax that was withheld from the
                  payments reported in Boxes 1, 3, and 8.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Withholding is required if recipients have not
                  provided their TIN.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5: Investment Expenses
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-investment-expen-img.webp" />
                  <source srcSet="/assets/images/1099-int-investment-expen-img.png" />
                  <img
                    src="/assets/images/1099-int-investment-expen-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-5-investment expenses"
                    width={277}
                    height={103}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This box is only relevant for single-class real estate
                  mortgage investment conduits (REMICs). See the instructions
                  under &quot;Rules for REMICs, FASITs, and Issuers of
                  CDOs&quot; for information about reporting investment expenses
                  in Box 5 for single-class REMICs.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b>The amount reported in Box 5 is not deductible
                  from your income.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Foreign Tax Paid
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-foreign-tax-img.webp" />
                  <source srcSet="/assets/images/1099-int-foreign-tax-img.png" />
                  <img
                    src="/assets/images/1099-int-foreign-tax-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-6-foreign tax paid"
                    width={277}
                    height={103}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any foreign tax paid on interest. Report the amount in
                  U.S. dollars.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Foreign Country or U.S. Territory
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-foreign-us-territory-img.webp" />
                  <source srcSet="/assets/images/1099-int-foreign-us-territory-img.png" />
                  <img
                    src="/assets/images/1099-int-foreign-us-territory-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-7-foreign country or u.s.territory "
                    width={277}
                    height={103}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the name of the foreign country or U.S. territory for
                  which the foreign tax was paid. Only applicable if you have
                  reported in box 6.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8: Tax-Exempt Interest
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-tax-exempt-img.webp" />
                  <source srcSet="/assets/images/1099-int-tax-exempt-img.png" />
                  <img
                    src="/assets/images/1099-int-tax-exempt-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-8-tax-exempt interest"
                    width={277}
                    height={103}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any tax-exempt interest of $10 or more (not including
                  tax-exempt original issue discount) that was paid or credited
                  to the person&apos;s account. This includes interest from
                  obligations issued by
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    a state, the District of Columbia, a U.S. territory, an
                    Indian tribal government, or their political subdivisions
                    (such as port authorities, toll road commissions, utility
                    services authorities, community redevelopment agencies,
                    etc.)
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    qualified volunteer fire departments to finance eligible
                    expenditures.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9: Specified Private Activity Bond Interest
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-private-act-img.webp" />
                  <source srcSet="/assets/images/1099-int-private-act-img.png" />
                  <img
                    src="/assets/images/1099-int-private-act-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-9-specified private activity bond interest"
                    width={277}
                    height={103}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter interest of $10 or more from specified private activity
                  bonds. <br />
                  Specified private activity bond means any private activity
                  bond defined in section 141 and issued after August 7, 1986.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 10: Market Discount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-market-dis-img.webp" />
                  <source srcSet="/assets/images/1099-int-market-dis-img.png" />
                  <img
                    src="/assets/images/1099-int-market-dis-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-10-market discount"
                    width={277}
                    height={103}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the recipient notified you that they made an election under
                  section 1278(b) for a covered security acquired with a market
                  discount, enter the amount of market discount that accrued on
                  that debt instrument during the tax year, if the amount is $10
                  or more.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 11: Bond Premium
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-bond-pre-img.webp" />
                  <source srcSet="/assets/images/1099-int-bond-pre-img.png" />
                  <img
                    src="/assets/images/1099-int-bond-pre-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-11-bond premium"
                    width={277}
                    height={103}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If a taxable bond was purchased above par value (at a
                  premium), report the portion of that premium amount that can
                  be amortized against the interest paid that year, unless the
                  bondholder instructed you they did not want to amortize the
                  premium.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 12. Bond Premium on Treasury Obligations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-treasury-oblig-img.webp" />
                  <source srcSet="/assets/images/1099-int-treasury-oblig-img.png" />
                  <img
                    src="/assets/images/1099-int-treasury-oblig-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-12-bond premium on treasury obligations"
                    width={277}
                    height={103}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If a U.S. Treasury security was purchased above par value (at
                  a premium), report the portion of that premium amount that can
                  be amortized against the interest paid that year, unless the
                  holder instructed you they did not want to amortize the
                  premium.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 13: Bond Premium on Tax-Exempt Bond
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-bond-tax-exempt-img.webp" />
                  <source srcSet="/assets/images/1099-int-bond-tax-exempt-img.png" />
                  <img
                    src="/assets/images/1099-int-bond-tax-exempt-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-13-Bond premium on tax-exempt bond"
                    width={277}
                    height={103}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If a tax-exempt bond was purchased above par value (at a
                  premium), report the portion of that premium amount that can
                  be amortized against the interest paid that year.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If you reported a net amount of interest in box
                  8 or 9, leave this box blank.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 14: Tax-Exempt and Tax Credit Bond CUSIP No.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-bond-tax-credit-img.webp" />
                  <source srcSet="/assets/images/1099-int-bond-tax-credit-img.png" />
                  <img
                    src="/assets/images/1099-int-bond-tax-credit-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-14-tax-exempt and tax credit bond cusip no."
                    width={277}
                    height={103}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }} component="div">
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      If reporting on a single tax-exempt bond, enter its CUSIP
                      in Box 14
                    </li>{' '}
                    <li style={{ marginBottom: 16 }}>
                      If reporting on a single tax credit bond, enter its CUSIP
                      in Box 14
                    </li>{' '}
                    <li style={{ marginBottom: 16 }}>
                      If aggregating interest/credits from multiple bonds, enter
                      &quot;various&quot;
                    </li>
                  </ul>
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 15-17: State information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-int-state-information-img.webp" />
                  <source srcSet="/assets/images/1099-int-state-information-img.png" />
                  <img
                    src="/assets/images/1099-int-state-information-img.png"
                    loading="lazy"
                    alt="Form 1099-int-box-15-17-state information"
                    width={544}
                    height={113}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }} component="div">
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      Box 15: Enter the 2-letter state abbreviation for the
                      state
                    </li>{' '}
                    <li style={{ marginBottom: 16 }}>
                      Box 16: Enter the state identification number
                    </li>{' '}
                    <li style={{ marginBottom: 16 }}>
                      Box 17: Report any state income tax withheld from the
                      payment
                    </li>
                  </ul>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form 1099-INT?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Form 1099-INT can be filed electronically or by postal mail.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Form 1099-INT electronic filing (E-filing)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS recommends{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-int/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-filing Form 1099-INT
                  </Link>{' '}
                  as the process is efficient and straightforward. E-filing
                  offers the advantage of receiving notifications promptly after
                  the IRS processes your submission.
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  With TaxZerone, the e-filing process is streamlined and can be
                  completed within minutes. You simply need to fill out the
                  form, review the information, transmit it to the IRS
                  electronically, and provide the recipient&apos;s copy.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you prefer to file Form 1099-INT via traditional paper
                  methods, here are the steps to follow:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download and print Form 1099-INT from the IRS website.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Complete the required fields, including the recipient&apos;s
                    name, address, TIN, and interest amount. Also, provide your
                    business name, address, and TIN.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Mail the completed paper form to the IRS at the{' '}
                    <Link
                      href="#1099-int-mailing-address"
                      sx={{ color: '#0202ff' }}
                    >
                      address specified
                    </Link>{' '}
                    in the 1099 instructions.
                  </li>
                  <li>
                    Send a copy of the 1099-INT to the recipient by January 31st
                    for their tax reporting purposes.
                  </li>
                </ol>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                >
                  <ReportProblemRoundedIcon
                    sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                  />{' '}
                  Important considerations for paper filing:
                </Typography>
                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    Mail the forms several weeks before the deadline to ensure
                    timely arrival at the IRS.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    You are required to provide a copy of the 1099-INT to the
                    recipient for accurate tax reporting on their part.
                  </li>
                  <li id="1099-int-mailing-address">
                    Paper filing involves printing, mailing, and tracking forms.
                    As an alternative, you can choose to file 1099-INT forms
                    electronically for faster processing and delivery.
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-INT - Mailing address
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you prefer paper filing, the mailing address for Form
                  1099-INT varies depending on your business location. Below is
                  a table summarizing the mailing address for form 1099-int:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-INT to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service <br />
                            Austin Submission Processing Center
                            <br />
                            P.O. Box 149213
                            <br />
                            Austin, TX 78714
                            <br />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            1973 North Rulon White Blvd.
                            <br />
                            Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Internal Revenue Service,
                            <br /> Austin Submission Processing Center,
                            <br /> P.O. Box 149213, <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form 1099-INT with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before initiating the e-filing process for Form 1099-INT,
                  it&apos;s essential to have all the necessary information
                  ready to ensure a smooth and efficient filing experience.
                  Here&apos;s what you&apos;ll need:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Required information for filing Form 1099-INT:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>Business details</li>
                  <li style={{ marginBottom: 16 }}>Recipient details</li>
                  <li style={{ marginBottom: 16 }}>Interest income</li>
                  <li style={{ marginBottom: 16 }}>Early withdrawal penalty</li>
                  <li style={{ marginBottom: 16 }}>
                    Amounts of bonds or treasury obligations
                  </li>
                  <li>Investment expenses</li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Once you have gathered all the required information, you can
                  proceed with the e-filing process using TaxZerone in three
                  simple steps:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 1: Complete Form 1099-INT</b>
                  <br />
                  Fill out the form with the necessary details, including payer
                  and recipient information, interest paid, and any federal
                  income tax withheld.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2: Review and transmit the return</b>
                  <br />
                  Carefully review the information entered to ensure accuracy,
                  and then transmit the completed Form 1099-INT to the IRS
                  electronically.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 3: Provide the recipient copy</b>
                  <br />
                  After transmitting the form to the IRS, send a copy of the
                  completed Form 1099-INT to the recipient for their tax
                  reporting purposes.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of e-filing Form 1099-INT with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  When you choose to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-int/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 1099-INT
                  </Link>{' '}
                  with TaxZerone, you can enjoy the following advantages:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        IRS form validations:
                      </span>{' '}
                      Ensure accuracy by checking your returns for potential
                      errors or missing information.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Email recipient copies:
                      </span>{' '}
                      Conveniently send recipient copies via email.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Secure portal access (ZeroneVault):
                      </span>{' '}
                      Provide recipients with secure access to their return
                      copies anytime, anywhere, through the ZeroneVault portal.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Competitive pricing:
                      </span>{' '}
                      Enjoy industry-leading pricing, with rates as low as $0.59
                      per form.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  By leveraging TaxZerone&apos;s e-filing solution, you can
                  streamline the Form 1099-INT filing process, ensuring
                  accuracy, efficiency, and compliance with IRS requirements.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Maximize your benefits by choosing TaxZerone <br />
              for e-filing Form 1099-INT
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099intinstructions`}
              >
                E-file Form 1099-INT
              </Button>
              {/* <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099INTInstructions;
